import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import {
  Upload,
  File,
  FileText,
  Image,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  GripVertical
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document categories
const DOCUMENT_CATEGORIES = [
  { value: 'pleadings', label: 'Pleadings', color: 'bg-blue-100 text-blue-700' },
  { value: 'discovery', label: 'Discovery', color: 'bg-purple-100 text-purple-700' },
  { value: 'correspondence', label: 'Correspondence', color: 'bg-green-100 text-green-700' },
  { value: 'evidence', label: 'Evidence', color: 'bg-amber-100 text-amber-700' },
  { value: 'contracts', label: 'Contracts', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'medical', label: 'Medical Records', color: 'bg-red-100 text-red-700' },
  { value: 'financial', label: 'Financial', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'court_orders', label: 'Court Orders', color: 'bg-orange-100 text-orange-700' },
  { value: 'client_docs', label: 'Client Documents', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'internal', label: 'Internal', color: 'bg-slate-100 text-slate-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

// Get file icon based on type
const getFileIcon = (file) => {
  const type = file.type || '';
  if (type.startsWith('image/')) return <Image className="w-5 h-5 text-purple-500" />;
  if (type.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
  if (type.includes('word') || type.includes('document')) return <FileText className="w-5 h-5 text-blue-500" />;
  return <File className="w-5 h-5 text-gray-500" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function DocumentUploadDialog({ 
  open, 
  onOpenChange, 
  caseId, 
  folderId = null,
  onUploadComplete 
}) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  // State for files with their categories
  const [fileItems, setFileItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [defaultCategory, setDefaultCategory] = useState('other');

  // Handle file selection
  const handleFileSelect = useCallback((files) => {
    const newItems = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      category: defaultCategory,
      status: 'pending' // pending, uploading, success, error
    }));
    setFileItems(prev => [...prev, ...newItems]);
  }, [defaultCategory]);

  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  // Handle file input change
  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
    // Reset input
    e.target.value = '';
  };

  // Update category for a specific file
  const updateFileCategory = (fileId, category) => {
    setFileItems(prev => 
      prev.map(item => 
        item.id === fileId ? { ...item, category } : item
      )
    );
  };

  // Remove a file from the list
  const removeFile = (fileId) => {
    setFileItems(prev => prev.filter(item => item.id !== fileId));
  };

  // Apply default category to all pending files
  const applyDefaultCategoryToAll = () => {
    setFileItems(prev => 
      prev.map(item => 
        item.status === 'pending' ? { ...item, category: defaultCategory } : item
      )
    );
  };

  // Upload all files
  const handleUpload = async () => {
    if (fileItems.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      
      // Append all files
      const categories = [];
      fileItems.forEach(item => {
        formData.append('files', item.file);
        categories.push(item.category);
      });
      
      // Append categories as JSON
      formData.append('categories', JSON.stringify(categories));
      
      // Append folder if specified
      if (folderId) {
        formData.append('folder_id', folderId);
      }

      // Mark all as uploading
      setFileItems(prev => prev.map(item => ({ ...item, status: 'uploading' })));

      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/documents/batch`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const result = await res.json();
        
        // Mark all as success
        setFileItems(prev => prev.map(item => ({ ...item, status: 'success' })));
        setUploadProgress(100);
        
        toast.success(`${result.uploaded} document(s) uploaded successfully`);
        
        // Close dialog and notify parent
        setTimeout(() => {
          setFileItems([]);
          setUploadProgress(0);
          onOpenChange(false);
          if (onUploadComplete) onUploadComplete();
        }, 1000);
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload documents');
      
      // Mark all as error
      setFileItems(prev => prev.map(item => ({ ...item, status: 'error' })));
    } finally {
      setUploading(false);
    }
  };

  // Reset dialog when closed
  const handleOpenChange = (open) => {
    if (!open) {
      setFileItems([]);
      setUploadProgress(0);
      setIsDragging(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
          <DialogDescription>
            Drag and drop files or click to select. You can categorize each document individually.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Drag and Drop Zone */}
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
              ${isDragging 
                ? 'border-primary bg-primary/5 scale-[1.02]' 
                : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
              }
            `}
            data-testid="drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleInputChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp"
            />
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
            <p className="text-lg font-medium mb-1">
              {isDragging ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-sm text-muted-foreground">
              or click to browse • PDF, Word, Excel, Images
            </p>
          </div>

          {/* Default Category Selector */}
          {fileItems.length > 0 && (
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <span className="text-sm font-medium">Default category:</span>
              <Select value={defaultCategory} onValueChange={setDefaultCategory}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={applyDefaultCategoryToAll}
                disabled={uploading}
              >
                Apply to All
              </Button>
            </div>
          )}

          {/* File List */}
          {fileItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{fileItems.length} file(s) selected</span>
                {!uploading && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setFileItems([])}
                    className="text-red-500 hover:text-red-700"
                  >
                    Clear All
                  </Button>
                )}
              </div>
              
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {fileItems.map((item) => (
                  <div 
                    key={item.id}
                    className={`
                      flex items-center gap-3 p-3 rounded-lg border transition-colors
                      ${item.status === 'success' ? 'bg-green-50 border-green-200' : ''}
                      ${item.status === 'error' ? 'bg-red-50 border-red-200' : ''}
                      ${item.status === 'uploading' ? 'bg-blue-50 border-blue-200' : ''}
                      ${item.status === 'pending' ? 'bg-white border-gray-200' : ''}
                    `}
                    data-testid={`file-item-${item.id}`}
                  >
                    {/* File Icon */}
                    <div className="flex-shrink-0">
                      {item.status === 'success' && <CheckCircle className="w-5 h-5 text-green-500" />}
                      {item.status === 'error' && <AlertCircle className="w-5 h-5 text-red-500" />}
                      {item.status === 'uploading' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />}
                      {item.status === 'pending' && getFileIcon(item.file)}
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(item.file.size)}</p>
                    </div>

                    {/* Category Selector */}
                    <Select 
                      value={item.category} 
                      onValueChange={(val) => updateFileCategory(item.id, val)}
                      disabled={uploading || item.status !== 'pending'}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Remove Button */}
                    {item.status === 'pending' && !uploading && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(item.id)}
                        className="flex-shrink-0 text-muted-foreground hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-sm text-center text-muted-foreground">
                Uploading documents...
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            disabled={uploading || fileItems.length === 0}
            data-testid="upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload {fileItems.length > 0 ? `${fileItems.length} File(s)` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
